/**
 * React Query hooks — single source of network truth for the SPA.
 *
 * Every household-scoped cache key includes the household_id (per ARCH §8.4)
 * so a household switch (queryClient.clear() + reload) is a clean break.
 *
 * Mutations attach `x-csrf: 1` automatically through `apiFetch`.
 *
 * Stale-while-revalidate: lists 30s, detail views 0 (always refresh after a
 * mutation). gcTime 5 minutes per ARCH §6.1.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  Account,
  AccountId,
  Category,
  CategoryId,
  HouseholdId,
  Intent,
  MembershipSummary,
  ParsedIntent,
  Person,
  Photo,
  PhotoId,
  PlaceDetail,
  PlaceId,
  PlaceStatus,
  PlaceSummary,
  Tag,
  TagId,
  Visit,
  VisitId,
  VisitItem,
  VisitItemId,
  VisitItemInput,
} from '@shared/types';
import { ApiError, apiFetch } from './api';

// ---------- Response shape helpers (mirrors of ARCH §2.2) ----------

interface SessionResponse {
  account: Pick<Account, 'id' | 'email' | 'display_name'>;
  active_household_id: HouseholdId | null;
}

interface MembershipsResponse {
  memberships: MembershipSummary[];
}

interface HouseholdsListResponse {
  households: Array<{
    id: HouseholdId;
    name: string;
    role: 'owner' | 'member';
    member_count: number;
    /** Household creation time. */
    created_at: string;
    /** When the active account joined this household. */
    joined_at: string;
  }>;
}

/** Per backend contract — `GET /api/visits` (household feed). */
export interface VisitFeedItem {
  id: VisitId;
  household_id: HouseholdId;
  place_id: PlaceId;
  place_name: string;
  category_id: CategoryId;
  category_name: string;
  visited_at: string;
  author_id: AccountId;
  rating: 1 | 2 | 3 | 4 | 5;
  notes: string | null;
}

interface VisitFeedResponse {
  visits: VisitFeedItem[];
}

interface PlacesListResponse {
  places: PlaceSummary[];
  next_cursor: string | null;
}

interface VisitsListResponse {
  visits: Visit[];
  next_cursor: string | null;
}

interface TrajectoryResponse {
  points: Array<{ visited_at: string; rating: number }>;
}

interface IntentsListResponse {
  intents: Intent[];
  next_cursor: string | null;
}

interface PeopleListResponse {
  people: Person[];
}

interface CategoriesListResponse {
  categories: Category[];
}

interface TagsListResponse {
  tags: Tag[];
}

interface ParseIntentResponse {
  intent_id: string;
  parsed: ParsedIntent;
  place_match: { place_id: PlaceId; name: string; score: number } | null;
  confidence: number;
}

interface ApplyIntentResponse {
  visit: Visit;
  place: PlaceDetail;
}

interface PhotoUrlResponse {
  /** Public signed-stream URL (drops directly into <img src>). */
  url: string;
  /** ISO-8601; ~15 min from issuance. */
  expires_at: string;
}

// ---------- Filter shapes ----------

export interface PlacesFilters {
  category?: CategoryId | CategoryId[];
  status?: PlaceStatus | PlaceStatus[];
  tag?: TagId | TagId[];
  q?: string;
  sort?: 'recent_visit' | 'recent_added' | 'name_asc' | 'rating_desc' | 'status';
  cursor?: string;
  limit?: number;
}

// ---------- Cache key factory (household-scoped) ----------

export const queryKeys = {
  me: () => ['me'] as const,
  session: () => ['session'] as const,
  memberships: () => ['memberships'] as const,
  households: () => ['households'] as const,

  // Every household-scoped key roots at the household_id so a switch is a
  // simple `queryClient.clear()` + full reload (per AC-016).
  categories: (hid: HouseholdId) => ['categories', hid] as const,
  tags: (hid: HouseholdId) => ['tags', hid] as const,
  places: (hid: HouseholdId, filters: PlacesFilters) =>
    ['places', hid, filters] as const,
  place: (hid: HouseholdId, placeId: PlaceId) =>
    ['place', hid, placeId] as const,
  visits: (hid: HouseholdId, placeId: PlaceId) =>
    ['visits', hid, placeId] as const,
  visit: (hid: HouseholdId, visitId: VisitId) =>
    ['visit', hid, visitId] as const,
  trajectory: (hid: HouseholdId, placeId: PlaceId) =>
    ['trajectory', hid, placeId] as const,
  people: (hid: HouseholdId, placeId: PlaceId) =>
    ['people', hid, placeId] as const,
  intents: (hid: HouseholdId) => ['intents', hid] as const,
  intent: (hid: HouseholdId, intentId: string) =>
    ['intent', hid, intentId] as const,
  /** Recent / household-feed visits. Used by Home + any cross-place view. */
  recentVisits: (hid: HouseholdId, limit = 50) =>
    ['recent-visits', hid, limit] as const,
  photoGet: (hid: HouseholdId, photoId: PhotoId) =>
    ['photo-get', hid, photoId] as const,
};

// ---------- Auth / Me ----------

export function useSession(
  options?: Omit<UseQueryOptions<SessionResponse, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<SessionResponse, Error>({
    queryKey: queryKeys.session(),
    queryFn: () => apiFetch<SessionResponse>('/auth/session'),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
    ...options,
  });
}

export function useMe(
  options?: Omit<UseQueryOptions<Account & { active_household_id: HouseholdId | null }, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Account & { active_household_id: HouseholdId | null }, Error>({
    queryKey: queryKeys.me(),
    queryFn: () =>
      apiFetch<Account & { active_household_id: HouseholdId | null }>('/me'),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
    ...options,
  });
}

export function useMemberships() {
  return useQuery<MembershipsResponse, Error>({
    queryKey: queryKeys.memberships(),
    queryFn: () => apiFetch<MembershipsResponse>('/me/memberships'),
    staleTime: 30_000,
  });
}

export function useRequestMagicLink() {
  return useMutation<{ ok: true }, Error, { email: string; return_to?: string }>({
    mutationFn: (body) => apiFetch('/auth/magic-link', { method: 'POST', body }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => apiFetch('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.clear();
    },
  });
}

export function useSetActiveHousehold() {
  return useMutation<
    { active_household_id: HouseholdId },
    Error,
    { household_id: HouseholdId }
  >({
    mutationFn: (body) =>
      apiFetch('/me/active-household', { method: 'POST', body }),
  });
}

// ---------- Households ----------

export function useHouseholds() {
  return useQuery<HouseholdsListResponse, Error>({
    queryKey: queryKeys.households(),
    queryFn: () => apiFetch<HouseholdsListResponse>('/households'),
    staleTime: 30_000,
  });
}

export function useCreateHousehold() {
  const qc = useQueryClient();
  return useMutation<
    { id: HouseholdId; name: string; created_at: string },
    Error,
    { name: string }
  >({
    mutationFn: (body) => apiFetch('/households', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.households() });
      qc.invalidateQueries({ queryKey: queryKeys.memberships() });
    },
  });
}

export function useAcceptInvite() {
  return useMutation<
    { household_id: HouseholdId; household_name: string },
    Error,
    { token: string }
  >({
    mutationFn: ({ token }) =>
      apiFetch(`/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        body: {},
      }),
  });
}

export function useInvitePreview(token: string | undefined) {
  return useQuery<{ household_name: string; inviter_email: string }, Error>({
    queryKey: ['invite-preview', token],
    queryFn: () =>
      apiFetch(`/invitations/${encodeURIComponent(token ?? '')}/preview`),
    enabled: Boolean(token),
    retry: false,
  });
}

// ---------- Categories ----------

export function useCategories(householdId: HouseholdId | null | undefined) {
  return useQuery<CategoriesListResponse, Error>({
    queryKey: householdId ? queryKeys.categories(householdId) : ['categories', 'inactive'],
    queryFn: () => apiFetch<CategoriesListResponse>('/categories'),
    enabled: Boolean(householdId),
    staleTime: 60_000,
  });
}

// ---------- Tags ----------

export function useTags(householdId: HouseholdId | null | undefined) {
  return useQuery<TagsListResponse, Error>({
    queryKey: householdId ? queryKeys.tags(householdId) : ['tags', 'inactive'],
    queryFn: () => apiFetch<TagsListResponse>('/tags'),
    enabled: Boolean(householdId),
    staleTime: 60_000,
  });
}

// ---------- Places ----------

export function usePlaces(
  householdId: HouseholdId | null | undefined,
  filters: PlacesFilters = {},
) {
  return useQuery<PlacesListResponse, Error>({
    queryKey: householdId
      ? queryKeys.places(householdId, filters)
      : ['places', 'inactive', filters],
    queryFn: () =>
      apiFetch<PlacesListResponse>('/places', {
        query: {
          ...(filters.category !== undefined ? { category: filters.category } : {}),
          ...(filters.status !== undefined ? { status: filters.status } : {}),
          ...(filters.tag !== undefined ? { tag: filters.tag } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.sort ? { sort: filters.sort } : {}),
          ...(filters.cursor ? { cursor: filters.cursor } : {}),
          ...(filters.limit ? { limit: filters.limit } : {}),
        },
      }),
    enabled: Boolean(householdId),
    staleTime: 30_000,
  });
}

export function usePlace(
  householdId: HouseholdId | null | undefined,
  placeId: PlaceId | null | undefined,
) {
  return useQuery<PlaceDetail, Error>({
    queryKey:
      householdId && placeId
        ? queryKeys.place(householdId, placeId)
        : ['place', 'inactive'],
    queryFn: () => apiFetch<PlaceDetail>(`/places/${placeId}`),
    enabled: Boolean(householdId && placeId),
    staleTime: 0,
  });
}

export interface CreatePlaceInput {
  name: string;
  category_id: CategoryId;
  /**
   * `null` to clear an existing address; `undefined` (omitted) to leave it
   * unchanged. The backend accepts both for PATCH semantics.
   */
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  status?: PlaceStatus;
  tag_ids?: TagId[];
}

export function useCreatePlace(householdId: HouseholdId | null | undefined) {
  const qc = useQueryClient();
  return useMutation<PlaceDetail, Error, CreatePlaceInput>({
    mutationFn: (body) => apiFetch('/places', { method: 'POST', body }),
    onSuccess: () => {
      if (householdId) {
        qc.invalidateQueries({ queryKey: ['places', householdId] });
      }
    },
  });
}

export function useUpdatePlace(
  householdId: HouseholdId | null | undefined,
  placeId: PlaceId,
) {
  const qc = useQueryClient();
  return useMutation<
    PlaceDetail,
    Error,
    Partial<CreatePlaceInput>,
    { previous: PlaceDetail | undefined }
  >({
    mutationFn: (body) =>
      apiFetch(`/places/${placeId}`, { method: 'PATCH', body }),
    // Optimistic — snapshot the current detail, apply the partial patch in
    // place so the UI reflects the change immediately (e.g. the status pill
    // animates straight to the new value). On error, restore.
    onMutate: async (patch) => {
      if (!householdId) return { previous: undefined };
      const key = queryKeys.place(householdId, placeId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PlaceDetail>(key);
      if (previous) {
        qc.setQueryData<PlaceDetail>(key, {
          ...previous,
          ...(patch.name !== undefined ? { name: patch.name } : {}),
          ...(patch.category_id !== undefined
            ? { category_id: patch.category_id }
            : {}),
          ...(patch.address !== undefined ? { address: patch.address } : {}),
          ...(patch.lat !== undefined ? { lat: patch.lat } : {}),
          ...(patch.lng !== undefined ? { lng: patch.lng } : {}),
          ...(patch.status !== undefined ? { status: patch.status } : {}),
        });
      }
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (householdId && context?.previous) {
        qc.setQueryData(queryKeys.place(householdId, placeId), context.previous);
      }
    },
    onSettled: () => {
      if (householdId) {
        qc.invalidateQueries({ queryKey: queryKeys.place(householdId, placeId) });
        qc.invalidateQueries({ queryKey: ['places', householdId] });
      }
    },
  });
}

export function useDeletePlace(householdId: HouseholdId | null | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, { placeId: PlaceId }>({
    mutationFn: ({ placeId }) =>
      apiFetch(`/places/${placeId}`, { method: 'DELETE' }),
    onSuccess: (_, { placeId }) => {
      if (householdId) {
        qc.invalidateQueries({ queryKey: queryKeys.place(householdId, placeId) });
        qc.invalidateQueries({ queryKey: ['places', householdId] });
      }
    },
  });
}

export function useTrajectory(
  householdId: HouseholdId | null | undefined,
  placeId: PlaceId | null | undefined,
) {
  return useQuery<TrajectoryResponse, Error>({
    queryKey:
      householdId && placeId
        ? queryKeys.trajectory(householdId, placeId)
        : ['trajectory', 'inactive'],
    queryFn: () => apiFetch<TrajectoryResponse>(`/places/${placeId}/trajectory`),
    enabled: Boolean(householdId && placeId),
    staleTime: 30_000,
  });
}

// ---------- Visits ----------

export function useVisits(
  householdId: HouseholdId | null | undefined,
  placeId: PlaceId | null | undefined,
) {
  return useQuery<VisitsListResponse, Error>({
    queryKey:
      householdId && placeId
        ? queryKeys.visits(householdId, placeId)
        : ['visits', 'inactive'],
    queryFn: () => apiFetch<VisitsListResponse>(`/places/${placeId}/visits`),
    enabled: Boolean(householdId && placeId),
    staleTime: 0,
  });
}

/** Fetch a single visit by id. Used by /visits/:id/edit. */
export function useVisit(
  householdId: HouseholdId | null | undefined,
  visitId: VisitId | null | undefined,
) {
  return useQuery<Visit, Error>({
    queryKey:
      householdId && visitId
        ? queryKeys.visit(householdId, visitId)
        : ['visit', 'inactive'],
    queryFn: () => apiFetch<Visit>(`/visits/${visitId}`),
    enabled: Boolean(householdId && visitId),
    staleTime: 0,
  });
}

export interface CreateVisitInput {
  place_id: PlaceId;
  visited_at: string;
  rating: 1 | 2 | 3 | 4 | 5;
  notes?: string;
  structured_json?: Record<string, unknown>;
  tag_ids?: TagId[];
  person_ids?: string[];
}

export function useCreateVisit(householdId: HouseholdId | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    Visit,
    Error,
    CreateVisitInput,
    {
      previousPlaceVisits: VisitsListResponse | undefined;
      previousRecentVisits: VisitFeedResponse | undefined;
      optimisticId: VisitId;
    }
  >({
    mutationFn: (body) => apiFetch('/visits', { method: 'POST', body }),
    // Optimistic — show the new visit at the top of the place timeline AND
    // the household feed immediately. The optimistic row gets a `tmp_` id so
    // we can spot/remove it if the server rejects.
    onMutate: async (input) => {
      const optimisticId = `tmp_${Date.now()}` as VisitId;
      const previousPlaceVisits = householdId
        ? qc.getQueryData<VisitsListResponse>(
            queryKeys.visits(householdId, input.place_id),
          )
        : undefined;
      const previousRecentVisits = householdId
        ? qc.getQueryData<VisitFeedResponse>(
            queryKeys.recentVisits(householdId),
          )
        : undefined;
      if (householdId) {
        await qc.cancelQueries({
          queryKey: queryKeys.visits(householdId, input.place_id),
        });
        await qc.cancelQueries({
          queryKey: queryKeys.recentVisits(householdId),
        });
      }
      return { previousPlaceVisits, previousRecentVisits, optimisticId };
    },
    onError: (_err, input, context) => {
      if (!householdId || !context) return;
      if (context.previousPlaceVisits) {
        qc.setQueryData(
          queryKeys.visits(householdId, input.place_id),
          context.previousPlaceVisits,
        );
      }
      if (context.previousRecentVisits) {
        qc.setQueryData(
          queryKeys.recentVisits(householdId),
          context.previousRecentVisits,
        );
      }
    },
    onSettled: (data) => {
      if (!householdId) return;
      const placeId = data?.place_id;
      if (placeId) {
        qc.invalidateQueries({
          queryKey: queryKeys.visits(householdId, placeId),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.place(householdId, placeId),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.trajectory(householdId, placeId),
        });
      }
      qc.invalidateQueries({ queryKey: ['places', householdId] });
      qc.invalidateQueries({ queryKey: queryKeys.intents(householdId) });
      qc.invalidateQueries({ queryKey: queryKeys.recentVisits(householdId) });
    },
  });
}

export function useUpdateVisit(
  householdId: HouseholdId | null | undefined,
  visitId: VisitId,
) {
  const qc = useQueryClient();
  return useMutation<
    Visit,
    Error,
    Partial<CreateVisitInput>,
    {
      previousVisit: Visit | undefined;
      previousPlaceVisits:
        | { placeId: PlaceId; data: VisitsListResponse }
        | undefined;
    }
  >({
    mutationFn: (body) =>
      apiFetch(`/visits/${visitId}`, { method: 'PATCH', body }),
    // Optimistic — apply the patch to the cached visit (e.g. notes edit
    // appears instantly). On error, restore both the single-visit cache and
    // the timeline list cache.
    onMutate: async (patch) => {
      if (!householdId) return { previousVisit: undefined, previousPlaceVisits: undefined };
      const visitKey = queryKeys.visit(householdId, visitId);
      await qc.cancelQueries({ queryKey: visitKey });
      const previousVisit = qc.getQueryData<Visit>(visitKey);
      if (previousVisit) {
        qc.setQueryData<Visit>(visitKey, {
          ...previousVisit,
          ...(patch.visited_at !== undefined
            ? { visited_at: patch.visited_at }
            : {}),
          ...(patch.rating !== undefined ? { rating: patch.rating } : {}),
          ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
        });
      }
      // Also try to update the place timeline list cache (if cached).
      let previousPlaceVisits:
        | { placeId: PlaceId; data: VisitsListResponse }
        | undefined;
      if (previousVisit) {
        const listKey = queryKeys.visits(householdId, previousVisit.place_id);
        const listData = qc.getQueryData<VisitsListResponse>(listKey);
        if (listData) {
          previousPlaceVisits = {
            placeId: previousVisit.place_id,
            data: listData,
          };
          qc.setQueryData<VisitsListResponse>(listKey, {
            ...listData,
            visits: listData.visits.map((v) =>
              v.id === visitId
                ? {
                    ...v,
                    ...(patch.visited_at !== undefined
                      ? { visited_at: patch.visited_at }
                      : {}),
                    ...(patch.rating !== undefined
                      ? { rating: patch.rating }
                      : {}),
                    ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
                  }
                : v,
            ),
          });
        }
      }
      return { previousVisit, previousPlaceVisits };
    },
    onError: (_err, _patch, context) => {
      if (!householdId || !context) return;
      if (context.previousVisit) {
        qc.setQueryData(
          queryKeys.visit(householdId, visitId),
          context.previousVisit,
        );
      }
      if (context.previousPlaceVisits) {
        qc.setQueryData(
          queryKeys.visits(
            householdId,
            context.previousPlaceVisits.placeId,
          ),
          context.previousPlaceVisits.data,
        );
      }
    },
    onSettled: (data) => {
      if (!householdId) return;
      if (data?.place_id) {
        qc.invalidateQueries({
          queryKey: queryKeys.visits(householdId, data.place_id),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.trajectory(householdId, data.place_id),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.place(householdId, data.place_id),
        });
      }
      qc.invalidateQueries({
        queryKey: queryKeys.visit(householdId, visitId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.recentVisits(householdId) });
    },
  });
}

export function useDeleteVisit(householdId: HouseholdId | null | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, { visitId: VisitId; placeId: PlaceId }>({
    mutationFn: ({ visitId }) =>
      apiFetch(`/visits/${visitId}`, { method: 'DELETE' }),
    onSuccess: (_, { placeId, visitId }) => {
      if (householdId) {
        qc.invalidateQueries({
          queryKey: queryKeys.visits(householdId, placeId),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.visit(householdId, visitId),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.trajectory(householdId, placeId),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.place(householdId, placeId),
        });
        qc.invalidateQueries({ queryKey: ['places', householdId] });
        qc.invalidateQueries({
          queryKey: queryKeys.recentVisits(householdId),
        });
      }
    },
  });
}

// ---------- Visit items (dishes / rooms / work performed) ----------

/**
 * All three item mutations invalidate the parent visit query (which embeds
 * items[]) plus the recent-visits feed so the timeline reflects changes.
 */
function invalidateVisitItemCaches(
  qc: ReturnType<typeof useQueryClient>,
  householdId: HouseholdId | null | undefined,
  visitId: VisitId,
) {
  if (!householdId) return;
  qc.invalidateQueries({ queryKey: queryKeys.visit(householdId, visitId) });
  qc.invalidateQueries({ queryKey: queryKeys.recentVisits(householdId) });
}

export function useCreateVisitItem(
  householdId: HouseholdId | null | undefined,
  visitId: VisitId,
) {
  const qc = useQueryClient();
  return useMutation<VisitItem, Error, VisitItemInput>({
    mutationFn: (body) =>
      apiFetch<VisitItem>(`/visits/${visitId}/items`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateVisitItemCaches(qc, householdId, visitId),
  });
}

export function useUpdateVisitItem(
  householdId: HouseholdId | null | undefined,
  visitId: VisitId,
) {
  const qc = useQueryClient();
  return useMutation<
    VisitItem,
    Error,
    { itemId: VisitItemId; patch: Partial<VisitItemInput> }
  >({
    mutationFn: ({ itemId, patch }) =>
      apiFetch<VisitItem>(`/items/${itemId}`, { method: 'PATCH', body: patch }),
    onSuccess: () => invalidateVisitItemCaches(qc, householdId, visitId),
  });
}

export function useDeleteVisitItem(
  householdId: HouseholdId | null | undefined,
  visitId: VisitId,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, { itemId: VisitItemId }>({
    mutationFn: ({ itemId }) =>
      apiFetch(`/items/${itemId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateVisitItemCaches(qc, householdId, visitId),
  });
}

// ---------- Tag attach / detach (per AC-119: optimistic) ----------

/**
 * Attach a tag to a place with optimistic `tag_ids` patch (AC-119). Adds the
 * tag id to the cached PlaceDetail.tag_ids immediately so the chip animates
 * in; on server reject we restore the prior snapshot.
 */
export function useAttachTag(
  householdId: HouseholdId | null | undefined,
  placeId: PlaceId,
) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { tagId: TagId },
    { previous: PlaceDetail | undefined }
  >({
    mutationFn: ({ tagId }) =>
      apiFetch(`/places/${placeId}/tags`, {
        method: 'POST',
        body: { tag_id: tagId },
      }),
    onMutate: async ({ tagId }) => {
      if (!householdId) return { previous: undefined };
      const key = queryKeys.place(householdId, placeId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PlaceDetail>(key);
      if (previous && !previous.tag_ids.includes(tagId)) {
        qc.setQueryData<PlaceDetail>(key, {
          ...previous,
          tag_ids: [...previous.tag_ids, tagId],
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (householdId && context?.previous) {
        qc.setQueryData(queryKeys.place(householdId, placeId), context.previous);
      }
    },
    onSettled: () => {
      if (householdId) {
        qc.invalidateQueries({ queryKey: queryKeys.place(householdId, placeId) });
        qc.invalidateQueries({ queryKey: ['places', householdId] });
      }
    },
  });
}

/**
 * Detach a tag from a place with optimistic `tag_ids` patch (AC-119).
 */
export function useDetachTag(
  householdId: HouseholdId | null | undefined,
  placeId: PlaceId,
) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { tagId: TagId },
    { previous: PlaceDetail | undefined }
  >({
    mutationFn: ({ tagId }) =>
      apiFetch(`/places/${placeId}/tags/${tagId}`, { method: 'DELETE' }),
    onMutate: async ({ tagId }) => {
      if (!householdId) return { previous: undefined };
      const key = queryKeys.place(householdId, placeId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<PlaceDetail>(key);
      if (previous && previous.tag_ids.includes(tagId)) {
        qc.setQueryData<PlaceDetail>(key, {
          ...previous,
          tag_ids: previous.tag_ids.filter((id) => id !== tagId),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (householdId && context?.previous) {
        qc.setQueryData(queryKeys.place(householdId, placeId), context.previous);
      }
    },
    onSettled: () => {
      if (householdId) {
        qc.invalidateQueries({ queryKey: queryKeys.place(householdId, placeId) });
        qc.invalidateQueries({ queryKey: ['places', householdId] });
      }
    },
  });
}

// ---------- People ----------

export function usePeople(
  householdId: HouseholdId | null | undefined,
  placeId: PlaceId | null | undefined,
) {
  return useQuery<PeopleListResponse, Error>({
    queryKey:
      householdId && placeId
        ? queryKeys.people(householdId, placeId)
        : ['people', 'inactive'],
    queryFn: () => apiFetch<PeopleListResponse>(`/places/${placeId}/people`),
    enabled: Boolean(householdId && placeId),
  });
}

// ---------- Photos ----------

export interface UploadPhotoInput {
  /** The compressed image file (after `compressImage`). */
  file: Blob;
  /** Attach to a place. Mutually exclusive with `visit_id`. */
  place_id?: PlaceId;
  /** Attach to a visit. Mutually exclusive with `place_id`. */
  visit_id?: VisitId;
  /** Optional ≤500 char caption. */
  caption?: string;
}

/**
 * Upload a photo via `POST /api/photos` (multipart). Replaces the prior
 * `useSignPutPhoto` + `useCommitPhoto` two-step. The Worker handles the
 * full pipeline (validate, EXIF strip, R2 put, D1 insert) in one request.
 *
 * Per backend contract:
 *   - `file` (Blob) is required; MIME must be image/jpeg|png|webp|heic; ≤10MB
 *   - Exactly one of `place_id` xor `visit_id`. The client validates locally too.
 *   - `caption` optional, ≤500 chars.
 */
export function useUploadPhoto(householdId: HouseholdId | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    Photo,
    Error,
    UploadPhotoInput,
    {
      tmpId: PhotoId;
      previousPlace: PlaceDetail | undefined;
      previousVisit: Visit | undefined;
    }
  >({
    mutationFn: async (input) => {
      // XOR enforcement on the client too — fail fast before the network call.
      const hasPlace = Boolean(input.place_id);
      const hasVisit = Boolean(input.visit_id);
      if (hasPlace === hasVisit) {
        throw new ApiError({
          code: 'VALIDATION',
          message: 'Attach the photo to exactly one of a place or a visit.',
          status: 400,
        });
      }

      const form = new FormData();
      // Name the file so the server has a hint (Worker still magic-byte sniffs).
      const filename =
        input.file instanceof File ? input.file.name : 'photo.jpg';
      form.append('file', input.file, filename);
      if (input.place_id) form.append('place_id', input.place_id);
      if (input.visit_id) form.append('visit_id', input.visit_id);
      if (input.caption) form.append('caption', input.caption);

      // We cannot route this through apiFetch (which JSON-stringifies the
      // body) — fetch directly with credentials + CSRF header. Browser sets
      // the multipart Content-Type with the correct boundary automatically.
      const res = await fetch('/api/photos', {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'x-csrf': '1',
        },
        body: form,
      });
      if (res.status === 204) return undefined as unknown as Photo;
      const text = await res.text();
      if (!res.ok) {
        let body:
          | { error?: { code?: string; message?: string } }
          | undefined;
        try {
          body = text ? JSON.parse(text) : undefined;
        } catch {
          body = undefined;
        }
        throw new ApiError({
          code: body?.error?.code ?? `HTTP_${res.status}`,
          message:
            body?.error?.message ??
            'We could not upload that photo.',
          status: res.status,
        });
      }
      return JSON.parse(text) as Photo;
    },
    // Optimistic — append a `tmp_` photo id to the cached gallery so the user
    // sees a thumbnail spot immediately (the gallery's per-photo signed-URL
    // fetch will resolve null until invalidate, but the slot animates in).
    // On success: invalidate so the real id replaces the placeholder.
    // On error: restore the snapshot.
    onMutate: async (input) => {
      const tmpId = `tmp_${Date.now()}` as PhotoId;
      let previousPlace: PlaceDetail | undefined;
      let previousVisit: Visit | undefined;
      if (!householdId) return { tmpId, previousPlace, previousVisit };
      if (input.place_id) {
        const key = queryKeys.place(householdId, input.place_id);
        await qc.cancelQueries({ queryKey: key });
        previousPlace = qc.getQueryData<PlaceDetail>(key);
        if (previousPlace) {
          qc.setQueryData<PlaceDetail>(key, {
            ...previousPlace,
            photo_ids: [tmpId, ...previousPlace.photo_ids],
            photo_count: previousPlace.photo_count + 1,
          });
        }
      }
      if (input.visit_id) {
        const key = queryKeys.visit(householdId, input.visit_id);
        await qc.cancelQueries({ queryKey: key });
        previousVisit = qc.getQueryData<Visit>(key);
        if (previousVisit) {
          qc.setQueryData<Visit>(key, {
            ...previousVisit,
            photo_ids: [tmpId, ...previousVisit.photo_ids],
          });
        }
      }
      return { tmpId, previousPlace, previousVisit };
    },
    onError: (_err, vars, context) => {
      if (!householdId || !context) return;
      if (vars.place_id && context.previousPlace) {
        qc.setQueryData(
          queryKeys.place(householdId, vars.place_id),
          context.previousPlace,
        );
      }
      if (vars.visit_id && context.previousVisit) {
        qc.setQueryData(
          queryKeys.visit(householdId, vars.visit_id),
          context.previousVisit,
        );
      }
    },
    onSuccess: (photo) => {
      if (householdId) {
        // Invalidate the parent of the photo so the gallery refetches with
        // the real photo id (the optimistic `tmp_` slot is replaced).
        if (photo.place_id) {
          qc.invalidateQueries({
            queryKey: queryKeys.place(householdId, photo.place_id),
          });
        }
        if (photo.visit_id) {
          qc.invalidateQueries({
            queryKey: queryKeys.visit(householdId, photo.visit_id),
          });
        }
      }
    },
  });
}

/**
 * Fetch a 15-min signed URL for a photo. The response field is `url`
 * (NOT `signed_url`) per the backend contract.
 *
 * The signed URL HMAC binds (household_id, photo_id, exp). A leaked URL
 * cannot be replayed against another household. We refetch ~14 min in to
 * stay under the 15-min TTL.
 */
export function usePhotoUrl(
  householdId: HouseholdId | null | undefined,
  photoId: PhotoId | null | undefined,
) {
  return useQuery<PhotoUrlResponse, Error>({
    queryKey:
      householdId && photoId
        ? queryKeys.photoGet(householdId, photoId)
        : ['photo-get', 'inactive'],
    queryFn: () => apiFetch<PhotoUrlResponse>(`/photos/${photoId}`),
    enabled: Boolean(householdId && photoId),
    // R2 signed URLs valid 15 min; refresh ~1 min before expiry.
    staleTime: 14 * 60_000,
  });
}

/**
 * Delete a photo with optimistic gallery patch (AC-119). The thumbnail
 * disappears immediately on user action; on server reject the cached
 * `photo_ids` array is restored.
 */
export function useDeletePhoto(householdId: HouseholdId | null | undefined) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { photoId: PhotoId; placeId?: PlaceId; visitId?: VisitId },
    {
      previousPlace: PlaceDetail | undefined;
      previousVisit: Visit | undefined;
    }
  >({
    mutationFn: ({ photoId }) =>
      apiFetch(`/photos/${photoId}`, { method: 'DELETE' }),
    onMutate: async ({ photoId, placeId, visitId }) => {
      let previousPlace: PlaceDetail | undefined;
      let previousVisit: Visit | undefined;
      if (!householdId) return { previousPlace, previousVisit };
      if (placeId) {
        const key = queryKeys.place(householdId, placeId);
        await qc.cancelQueries({ queryKey: key });
        previousPlace = qc.getQueryData<PlaceDetail>(key);
        if (previousPlace) {
          qc.setQueryData<PlaceDetail>(key, {
            ...previousPlace,
            photo_ids: previousPlace.photo_ids.filter((id) => id !== photoId),
            photo_count: Math.max(0, previousPlace.photo_count - 1),
          });
        }
      }
      if (visitId) {
        const key = queryKeys.visit(householdId, visitId);
        await qc.cancelQueries({ queryKey: key });
        previousVisit = qc.getQueryData<Visit>(key);
        if (previousVisit) {
          qc.setQueryData<Visit>(key, {
            ...previousVisit,
            photo_ids: previousVisit.photo_ids.filter((id) => id !== photoId),
          });
        }
      }
      return { previousPlace, previousVisit };
    },
    onError: (_err, vars, context) => {
      if (!householdId || !context) return;
      if (vars.placeId && context.previousPlace) {
        qc.setQueryData(
          queryKeys.place(householdId, vars.placeId),
          context.previousPlace,
        );
      }
      if (vars.visitId && context.previousVisit) {
        qc.setQueryData(
          queryKeys.visit(householdId, vars.visitId),
          context.previousVisit,
        );
      }
    },
    onSettled: (_data, _err, { placeId, visitId }) => {
      if (!householdId) return;
      if (placeId) {
        qc.invalidateQueries({
          queryKey: queryKeys.place(householdId, placeId),
        });
      }
      if (visitId) {
        qc.invalidateQueries({
          queryKey: queryKeys.visit(householdId, visitId),
        });
      }
    },
  });
}

// ---------- Visit feed (household-wide) ----------

/**
 * Household-wide recent visits feed. Backend route: `GET /api/visits?limit=N`.
 * Response includes `place_name`, `category_id`, `category_name` joined in.
 * Sort: visited_at DESC, id DESC. Default limit 50.
 */
export function useRecentVisits(
  householdId: HouseholdId | null | undefined,
  limit = 50,
) {
  return useQuery<VisitFeedResponse, Error>({
    queryKey: householdId
      ? queryKeys.recentVisits(householdId, limit)
      : ['recent-visits', 'inactive'],
    queryFn: () =>
      apiFetch<VisitFeedResponse>('/visits', { query: { limit } }),
    enabled: Boolean(householdId),
    staleTime: 15_000,
    retry: false,
  });
}

// ---------- Intents (NL capture pipeline) ----------

export function useIntents(householdId: HouseholdId | null | undefined) {
  return useQuery<IntentsListResponse, Error>({
    queryKey: householdId ? queryKeys.intents(householdId) : ['intents', 'inactive'],
    queryFn: () => apiFetch<IntentsListResponse>('/intents'),
    enabled: Boolean(householdId),
  });
}

/**
 * Parse — writes an `intents` row with applied_at = NULL BEFORE returning
 * (per UX-AC-024 / AC-082). The card UI never auto-saves.
 */
export function useParseIntent() {
  return useMutation<ParseIntentResponse, Error, { rawText: string }>({
    mutationFn: (body) =>
      apiFetch('/intents/parse', { method: 'POST', body }),
  });
}

export interface ApplyIntentInput {
  /**
   * The full, user-confirmed ParsedIntent (server re-validates the full shape
   * for security; partial overrides are NOT accepted). The frontend should
   * merge any local edits into the parsed value and pass the complete result.
   */
  confirmed: ParsedIntent;
  place_id?: PlaceId | 'new';
  new_place?: {
    name: string;
    category_id: CategoryId;
    address?: string;
    lat?: number;
    lng?: number;
  };
}

/**
 * Apply — only fires when the user explicitly taps Save on the confirmation
 * card. No `onMutate`/optimistic update (per AC-119) — we need the server IDs.
 */
export function useApplyIntent(
  householdId: HouseholdId | null | undefined,
  intentId: string,
  options?: Omit<UseMutationOptions<ApplyIntentResponse, Error, ApplyIntentInput>, 'mutationFn'>,
) {
  const qc = useQueryClient();
  return useMutation<ApplyIntentResponse, Error, ApplyIntentInput>({
    mutationFn: (body) =>
      apiFetch(`/intents/${encodeURIComponent(intentId)}/apply`, {
        method: 'POST',
        body,
      }),
    ...options,
    onSuccess: (data, variables, ...rest) => {
      if (householdId) {
        qc.invalidateQueries({ queryKey: queryKeys.intents(householdId) });
        qc.invalidateQueries({ queryKey: ['places', householdId] });
        qc.invalidateQueries({
          queryKey: queryKeys.place(householdId, data.place.id),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.visits(householdId, data.place.id),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.trajectory(householdId, data.place.id),
        });
        qc.invalidateQueries({
          queryKey: queryKeys.recentVisits(householdId),
        });
      }
      // Forward all positional args (signature varies across React Query
      // versions — we intentionally pass-through with the spread).
      (options?.onSuccess as undefined | ((...a: unknown[]) => unknown))?.(
        data,
        variables,
        ...rest,
      );
    },
  });
}
